import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useOutletContext } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { Row, Col } from 'react-bootstrap';
// icons
import { FiEdit } from 'react-icons/fi';
import { RiDeleteBinLine } from 'react-icons/ri';
//api
import { deleteBlogCategory } from 'api/blog';
import { getBlogCategories } from 'api/pathForHook/blog';
//utils
import useHandleSearch from 'utils/customHooks/useHandleSearch';
//components
import CustomTable from 'components/backoffice/customTable';
import Button from 'components/backoffice/buttonCustom';
import DeleteModalCustom from 'components/backoffice/modals/DeleteModalCustom';
import Title from 'components/backoffice/texts/title';
import { usePermissions } from 'components/backoffice/PermissionsContext';
import SearchTable from 'components/backoffice/table/SearchTable';

const BlogCategories = () => {
  const userPermissions = usePermissions();
  const navigate = useNavigate();
  const [blogCategories, setBlogCategories] = useState([]);
  const [page, setPage] = useState(1);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState({});
  const [inputValue, setInputValue] = useState('');
  const [responseInfo, setResponseInfo] = useState({});
  const { t } = useTranslation(['backoffice']);
  const { setTitle } = useOutletContext()

  const colsPosts = [t('categoryName'), t('actions')];

  const { fetchData, stateData, loading } = useHandleSearch();

  const getBlogCategoriesList = async () => {
    try {
      let payload = {
        page,
        page_size: 10,
        search: inputValue || '',
        fields: 'id,name'
      }
      await fetchData(getBlogCategories(), payload);
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    setTitle(t('blogCategories'))
    return () => {
      setTitle('');
    };
  }, [setTitle]);

  useEffect(() => {
    const { results, errors, totalPages, noResults } = stateData;
    if (results !== null) {
      setBlogCategories(results);
      setResponseInfo({ errors, totalPages, noResults });
    } else {
      setBlogCategories(null);
      setResponseInfo({ errors });
    }
  }, [stateData])

  useEffect(() => {
    if (page > 1 && inputValue !== '') {
      setPage(1);
    }
  }, [inputValue])

  useEffect(() => {
    getBlogCategoriesList();
  }, [page, inputValue]);

  const handleCreate = () => {
    navigate('/intranet/blog-categories/create');
  };

  const onDelete = async () => {
    const { status } = await deleteBlogCategory(selectedCategory.id);
    if (status === 204) {
      toast.success(t('eliminadocorrectamente'));
      getBlogCategoriesList();
      setShowDeleteModal(false);
    }
  };

  const openModalDelete = (object) => {
    setShowDeleteModal(true);
    setSelectedCategory(object);
  };

  const handleClear = () => {
    setInputValue('');
    setPage(1);
    setResponseInfo(prevResponseInfo => ({ ...prevResponseInfo, noResults: false }));
    getBlogCategoriesList();
  };

  return (
    <div className="d-lg-grid">
      <Title text={t('blogCategories')} />
      <Row className="d-flex w-md-100 mt-3">
        <Col md={5} xs={12} className="d-flex text-start">
          <SearchTable
            value={inputValue}
            handleSearch={setInputValue}
            handleClear={handleClear}
            handleKeyDown={true}
            placeholder={t('searchbyname')}
          />
        </Col>
        <Col md={7} xs={12} className="text-end mt-3 mt-md-0">
          {userPermissions.includes('category.__add') && (
            <Button color={'primary'} onClick={handleCreate}>
              {t('crear')}
            </Button>
          )}
        </Col>
      </Row>
      <div className="table-responsive">
        <CustomTable
          cols={colsPosts}
          noResults={responseInfo?.noResults}
          isLoading={loading}
          page={page}
          changePage={setPage}
          totalPages={responseInfo?.totalPages}
        >
          {blogCategories &&
            blogCategories.map((category, index) => {
              return (
                <tr key={category.id}>
                  <td>{category.name}</td>
                  <td className="text-end">
                    {userPermissions.includes('category.__modify') && (
                      <Link to={`/intranet/blog-categories/edit/${category.id}`}>
                        <FiEdit className="mx-3" color="white" size={20} />
                      </Link>
                    )}
                    {userPermissions.includes('category.__delete') && (
                      <RiDeleteBinLine
                        className="mx-3 style-icon-delete"
                        size={20}
                        onClick={() => openModalDelete(category)}
                      />
                    )}
                  </td>
                </tr>
              );
            })}
        </CustomTable>
      </div>
      <DeleteModalCustom
        name={selectedCategory?.name}
        openModal={showDeleteModal}
        setOpenModal={setShowDeleteModal}
        handleDelete={onDelete}
        deleteTitle={t('deleteblogcategory')}
        beforeTitle={t('blogCategories')}
      />
    </div>
  );
};

export default BlogCategories;